import { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import {
  getLtcPool,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { carePhaseLabel, careEnvironmentLabel } from '../../glossary/enumLabels';
import { currency, resolveMonthDisplay } from './formatters';
import { milestoneBadgeClass } from './milestoneColors';
import { ReasonBreakdown, buildReasonTooltips } from './reasonBreakdown';
import { breakdownLines, reasonForMonth } from './segments';
import type { StrategyPresentation } from './strategies/types';

const rangeLabel = (start: string | number, end: string | number) =>
  start === end ? String(start) : `${start}–${end}`;

function isHomeWaitingPeriodBucket(careEnvironment: CareEnvironment): boolean {
  return careEnvironment === CareEnvironment.home;
}

export function WhenTooltipContent({
  startMonth,
  endMonth,
  terms,
}: {
  startMonth: MonthlyEntry;
  endMonth?: MonthlyEntry;
  terms?: ClaimTerms;
}) {
  const end = endMonth ?? startMonth;
  const ageText =
    startMonth.context.clientAge != null && end.context.clientAge != null
      ? rangeLabel(startMonth.context.clientAge, end.context.clientAge)
      : '—';
  // A policy can't pay for care before its premium-start year, so rows whose
  // policy year is < 1 are "Pre-policy" rather than "Policy Year 0/−1".
  const policyYearText =
    startMonth.context.policyYear < 1 && end.context.policyYear < 1
      ? 'Pre-policy'
      : rangeLabel(startMonth.context.policyYear, end.context.policyYear);
  const startPhase = `${carePhaseLabel(startMonth.context.carePhase)} at ${careEnvironmentLabel(startMonth.context.careEnvironment)}`;
  const endPhase = `${carePhaseLabel(end.context.carePhase)} at ${careEnvironmentLabel(end.context.careEnvironment)}`;
  const phaseText = startPhase === endPhase ? startPhase : `${startPhase} → ${endPhase}`;

  // Policy anniversary is anchored to row 0 of the projection (`floor(monthIndex / 12)`
  // ticks the policy year). Show how many rows until the next tick so the cadence is
  // explicit; year-only granularity on `policyPremiumStartYear` means we can't show a
  // month/day.
  const monthsIntoPolicyYear = ((startMonth.context.monthIndex % 12) + 12) % 12;
  const monthsToNextAnniversary = monthsIntoPolicyYear === 0 ? 12 : 12 - monthsIntoPolicyYear;

  return (
    <TooltipContent className="text-xs">
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Age</span>
        <span className="font-semibold">{ageText}</span>
        <span className="text-gray-300">Policy Year</span>
        <span className="font-semibold">{policyYearText}</span>
        <span className="text-gray-300">Care Phase</span>
        <span className="font-semibold">{phaseText}</span>
        {terms && (
          <>
            <span className="text-gray-300">Policy started</span>
            <span className="font-semibold">{terms.policyPremiumStartYear}</span>
            <span className="text-gray-300">Next anniversary</span>
            <span className="font-semibold">in {monthsToNextAnniversary} mo</span>
          </>
        )}
      </div>
    </TooltipContent>
  );
}

export function WhenCell({
  month,
  terms,
  monthDisplayMap,
}: {
  month: MonthlyEntry;
  terms?: ClaimTerms;
  // Per-claimant relative-month map (joint policies). Single-life or omitted
  // falls back to absolute `monthIndex + 1`. The tooltip still shows the
  // calendar-anchored policy year — only the headline "Month N" is rescaled
  // per claimant.
  monthDisplayMap?: Map<string, number>;
}) {
  const milestones = month.events.milestones;

  return (
    <div className="flex flex-col gap-1.5 max-w-56">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block text-sm font-semibold leading-snug text-gray-900 cursor-help">
            Month {resolveMonthDisplay(month.context.monthIndex, month.context.claimant, monthDisplayMap)}
          </span>
        </TooltipTrigger>
        <WhenTooltipContent startMonth={month} terms={terms} />
      </Tooltip>
      {milestones.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {milestones.map((m, mi) => (
            <span
              key={mi}
              className={`whitespace-normal rounded px-2 py-0.5 text-xs font-medium ${milestoneBadgeClass(m.kind)}`}
            >
              {m.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CareCell({ month }: { month: MonthlyEntry }) {
  return (
    <div className="text-xs text-gray-800">
      <span className="font-medium">{carePhaseLabel(month.context.carePhase)}</span>
      <span className="mx-1 text-gray-400">/</span>
      <span className="text-gray-600">{careEnvironmentLabel(month.context.careEnvironment)}</span>
    </div>
  );
}

export function CostCell({
  month,
  terms,
}: {
  month: MonthlyEntry;
  terms: ClaimTerms;
}) {
  // Read the engine's own per-row inflation breakdown. `computeMonthlyCostMetrics`
  // resolves these claimant-relative, so they're correct for joint partner rows
  // too — unlike deriving from session-level `terms` + the merged-stream
  // `monthIndex`, which mislabels the partner (it inherits the session client's
  // onset year and the merged stream offset).
  const yearsFromToday = month.facts.careCostInflationYears;
  const projectedYear = month.facts.projectedCareYear;
  // The rate the ENGINE grew this row at. Claimant-relative like the two facts
  // above, so a joint partner with their own healthcare-inflation setting gets
  // an equation that reconciles; `terms.careCostInflationRate` is the session
  // client's and would misstate the partner's rows.
  const inflationRate = month.facts.careCostInflationRate ?? terms.careCostInflationRate;
  // The Care cost column shows the full monthly cost the client faces. For
  // Independent Living that is care (priced and covered as home care) PLUS
  // out-of-pocket room & board; `roomAndBoardCost` is 0 for every other
  // environment, so those rows are unchanged. Payout/coverage math still prices
  // against the care-only `effectiveCareCost` — this addition is display only.
  const careCost = month.facts.effectiveCareCost;
  const roomAndBoard = month.facts.roomAndBoardCost ?? 0;
  const hasRoomAndBoard = roomAndBoard > 0;
  const totalCost = careCost + roomAndBoard;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">{currency(totalCost)}</div>
          <div className="text-[10px] text-gray-500">
            {hasRoomAndBoard
              ? `incl. ${currency(roomAndBoard)} room & board`
              : `${currency(month.facts.dailyCareCost)}/day`}
          </div>
        </div>
      </TooltipTrigger>
      {hasRoomAndBoard ? (
        <CareCostBreakdownTooltip
          projectedYear={projectedYear}
          nominalCareCost={month.facts.nominalCareCost}
          nominalRoomAndBoard={month.facts.nominalRoomAndBoardCost ?? 0}
          roomAndBoardCovered={month.deltas.payout.roomAndBoardCovered ?? 0}
          rate={inflationRate}
          years={yearsFromToday}
          careCost={careCost}
          roomAndBoard={roomAndBoard}
          totalCost={totalCost}
        />
      ) : (
        <InflationTooltipContent
          header={`Projected year ${projectedYear}`}
          // `yearsFromToday` (careCostInflationYears) counts from the engine's
          // current-year anchor, so the base is priced in today's dollars — keep
          // the label anchor-agnostic rather than naming the plan-created year.
          startingLabel="Monthly cost (today's dollars)"
          startingValue={month.facts.nominalCareCost}
          rate={inflationRate}
          years={yearsFromToday}
          result={careCost}
        />
      )}
    </Tooltip>
  );
}

// One "today's dollars × growth = result" derivation block, as grid rows. Both
// halves of the Independent Living care-cost tooltip use it so care and room &
// board are shown to reach their figures the same way.
function InflationDerivationRows({
  resultLabel,
  nominal,
  rate,
  years,
  result,
}: {
  resultLabel: string;
  nominal: number;
  rate: number;
  years: number;
  result: number;
}) {
  return (
    <>
      <span className="text-gray-300">Monthly cost (today's dollars)</span>
      <span className="font-semibold tabular-nums">{currency(nominal)}</span>
      <span className="text-gray-300">Compound growth</span>
      <span className="font-semibold tabular-nums">
        &times; (1 + {(rate * 100).toFixed(2)}%)<sup>{years}</sup>
      </span>
      <span className="text-gray-300">{resultLabel}</span>
      <span className="font-semibold tabular-nums">{currency(result)}</span>
    </>
  );
}

// Care cost cell tooltip for rows that carry room & board (Independent Living).
// Shows how BOTH figures were reached — each is its own today's-dollars ×
// compound-growth derivation on the same row clock — before composing them into
// the column's total. Showing only the split (the previous shape) left the two
// inflated numbers unexplained, which is the one thing this tooltip exists to
// answer.
//
// This is a COST breakdown: every figure is gross, matching the column's
// headline. It deliberately does not net out `roomAndBoardCovered` — the Care
// cost column shows what care costs, the Policy payout column shows what was
// met. Calling the whole room & board "out of pocket" here would contradict the
// adjacent payout tooltip on an indemnity row, where this month's cash can meet
// part of it, so the covered amount is named instead.
function CareCostBreakdownTooltip({
  projectedYear,
  nominalCareCost,
  nominalRoomAndBoard,
  roomAndBoardCovered,
  rate,
  years,
  careCost,
  roomAndBoard,
  totalCost,
}: {
  projectedYear: number;
  nominalCareCost: number;
  nominalRoomAndBoard: number;
  roomAndBoardCovered: number;
  rate: number;
  years: number;
  careCost: number;
  roomAndBoard: number;
  totalCost: number;
}) {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">Projected year {projectedYear}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <InflationDerivationRows
          resultLabel="Care result"
          nominal={nominalCareCost}
          rate={rate}
          years={years}
          result={careCost}
        />
        <span className="col-span-2 my-1 border-t border-gray-700" />
        <InflationDerivationRows
          resultLabel="Room & board result"
          nominal={nominalRoomAndBoard}
          rate={rate}
          years={years}
          result={roomAndBoard}
        />
        <span className="col-span-2 my-1 border-t border-gray-700" />
        <span className="col-span-2 text-[10px] uppercase tracking-wide text-gray-400">
          Total cost
        </span>
        <span className="text-gray-300">Care (covered as home)</span>
        <span className="font-semibold tabular-nums">{currency(careCost)}</span>
        <span className="text-gray-300">Room &amp; board</span>
        <span className="font-semibold tabular-nums">{currency(roomAndBoard)}</span>
        <span className="col-span-2 my-1 border-t border-gray-700" />
        <span className="text-gray-300">Total monthly cost</span>
        <span className="font-semibold tabular-nums">{currency(totalCost)}</span>
      </div>
      <div className="mt-1.5 text-[10px] text-gray-400">
        Independent living care is priced and covered like home care. Room &amp; board is not part
        of the care benefit
        {roomAndBoardCovered > 0 ? (
          <>
            , but {currency(roomAndBoardCovered)} of it was met by this month&apos;s benefit — see
            Policy payout.
          </>
        ) : (
          <> and is paid out of pocket.</>
        )}
      </div>
    </TooltipContent>
  );
}

// Tooltip derivation inputs for the engine's applied monthly cap
// (`month.facts.appliedMonthlyBenefitCap`): the at-issue monthly for the
// row's care environment, and the rate/cap-years of the pool the engine grew
// the cap on (`facts.appliedCapPoolSource`). Display-only — presentations
// must headline the engine's cap value itself, never recompute it.
export function appliedCapTooltipInputs(month: MonthlyEntry, terms: ClaimTerms) {
  const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
  const baseDaily =
    dailyByEnvironment[month.context.careEnvironment] ?? terms.nominalDailyBenefitBase;
  const fromEob = month.facts.appliedCapPoolSource === 'eob';
  const rate = fromEob
    ? terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate
    : terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const capYears = fromEob
    ? terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears
    : terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  return { baseMonthly: baseDaily * DAYS_IN_MONTH, rate, capYears, fromEob };
}

export function BenefitAmountCell({
  snapshot,
  terms,
  presentation,
}: {
  snapshot: YearSnapshot;
  terms: ClaimTerms;
  presentation?: StrategyPresentation;
}) {
  const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
  const intYear = Math.floor(snapshot.policyYear);
  const cap = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
  // A capYears-year rider applies capYears increases (exponent = policyYear − 1,
  // capped at capYears), matching the engine's `compoundFactor`.
  const rawIncrements = Math.max(intYear - 1, 0);
  const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && intYear > cap;
  const note = capped
    ? (presentation?.copy?.benefitInflationCapNote?.(cap) ?? `Inflation capped at year ${cap}`)
    : undefined;
  const isSimple = terms.inflationIsSimple === true;
  // Strategy "compound" label copy only applies when actually compounding.
  // Simple-form CM II falls back to a neutral label so a "Compound rider
  // growth" override doesn't mislabel linear growth.
  const growthLabel = isSimple
    ? 'Simple growth'
    : presentation?.copy?.benefitInflationCompoundLabel ?? 'Compound growth';
  const footer =
    presentation?.benefitAmountCellTooltipFooter?.({ snapshot, terms }) ?? null;
  // Headline = the per-month benefit ceiling this year. For CM II that's
  // baseMonthly + riderMonthly; for Asset Flex it's the ACIO-inflated
  // monthly cap. Both products store this on `monthlyBenefitCap`. The
  // tooltip formula uses the strategy's per-product rate (Asset Flex's
  // On-Face ACIO can differ from policy-wide ACIO) and the simple-vs-
  // compound mode flagged in `terms.inflationIsSimple`.
  const rate = presentation?.benefitInflationRate?.(terms) ?? terms.inflationRate;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">
            {currency(snapshot.monthlyBenefitCap)}
          </div>
          <div className="text-[10px] text-gray-500">
            {currency(snapshot.effectiveDailyBenefit)}/day
          </div>
        </div>
      </TooltipTrigger>
      <InflationTooltipContent
        header={`Policy year ${intYear} · started ${terms.policyPremiumStartYear}`}
        startingLabel={`At issue (${terms.policyPremiumStartYear})`}
        startingValue={baseMonthly}
        rate={rate}
        years={increments}
        result={snapshot.monthlyBenefitCap}
        note={note}
        compoundLabel={growthLabel}
        isSimple={isSimple}
        footer={footer}
      />
    </Tooltip>
  );
}

export function InflationTooltipContent({
  header,
  startingLabel,
  startingValue,
  rate,
  years,
  result,
  note,
  compoundLabel,
  isSimple = false,
  footer,
}: {
  header?: string;
  startingLabel: string;
  startingValue: number;
  rate: number;
  years: number;
  result: number;
  note?: string;
  compoundLabel?: string;
  isSimple?: boolean;
  footer?: ReactNode;
}) {
  const ratePct = (rate * 100).toFixed(2);
  // Fall back to a growth-mode-aware label when a caller doesn't supply its own
  // descriptive copy, so simple-growth rows aren't mislabeled as compound.
  const growthLabel = compoundLabel ?? (isSimple ? 'Simple growth' : 'Compound growth');
  return (
    <TooltipContent className="text-xs">
      {header && <div className="mb-1 font-semibold">{header}</div>}
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">{growthLabel}</span>
        <span className="font-semibold tabular-nums">
          {isSimple ? (
            <>× (1 + {ratePct}% × {years})</>
          ) : (
            <>× (1 + {ratePct}%)<sup>{years}</sup></>
          )}
        </span>
        <span className="text-gray-300">Result</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        {note && (
          <>
            <span />
            <span className="text-[10px] text-amber-300">{note}</span>
          </>
        )}
      </div>
      {footer && <div className="mt-2 border-t border-gray-700 pt-1">{footer}</div>}
    </TooltipContent>
  );
}

// Forward-summed "Total LTC Benefit" cell tooltip. Reconciles the carrier's
// printed total with the two pool columns: the total = the current pool
// principal PLUS the inflation that accrues on the monthly benefit while the
// claim is being paid. Surfacing the split keeps the timeline's total from
// reading as a contradiction with the (smaller) opening pool a claim carries
// in. Shared across products that print a forward-inflated total (Asset Care:
// AOB + COB; Asset Flex: AOB + EOB) — they differ only in the heading prefix,
// the description copy, and the pool label.
export function ForwardTotalTooltip({
  policyYear,
  principal,
  total,
  headingPrefix,
  description,
  poolLabel,
}: {
  policyYear: number;
  principal: number;
  total: number;
  headingPrefix: string;
  description: ReactNode;
  poolLabel: string;
}) {
  const inClaimInflation = Math.max(total - principal, 0);
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">
        {headingPrefix} {policyYear} · Total LTC benefit
      </div>
      <div className="mb-2 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{poolLabel} pool now</span>
        <span className="font-semibold tabular-nums">{currency(principal)}</span>
        <span className="text-gray-300">+ inflation during the claim</span>
        <span className="font-semibold tabular-nums">{currency(inClaimInflation)}</span>
        <span className="text-gray-300">Total LTC benefit</span>
        <span className="font-semibold tabular-nums">{currency(total)}</span>
      </div>
    </TooltipContent>
  );
}

export function EligibilityCell({
  month,
  openState,
}: {
  month: MonthlyEntry;
  openState: MonthlyState;
}) {
  const eligible = month.facts.coverageEligibleDays;
  const non2Adl = month.facts.non2AdlDays;
  const wpHome = month.facts.waitingPeriodDaysConsumedHome;
  const wpFacility = month.facts.waitingPeriodDaysConsumedFacility;
  const isHomeBucket = isHomeWaitingPeriodBucket(month.context.careEnvironment);
  const wpConsumed = isHomeBucket ? wpHome : wpFacility;
  const wpRemaining = isHomeBucket
    ? month.state.waitingPeriod.homeDays
    : month.state.waitingPeriod.facilityDays;
  const ineligible = Math.max(DAYS_IN_MONTH - eligible, 0);

  const reasons: { label: string; tone: string }[] = [];
  if (non2Adl > 0) {
    reasons.push({ label: `${non2Adl}d <2 ADL`, tone: 'bg-amber-50 text-amber-800' });
  }
  if (wpConsumed > 0) {
    reasons.push({ label: `${wpConsumed}d in WP`, tone: 'bg-rose-50 text-rose-800' });
  }

  const wpWasActive = isHomeBucket
    ? openState.waitingPeriod.homeDays > 0
    : openState.waitingPeriod.facilityDays > 0;

  return (
    <div className="leading-tight">
      <div>
        <span className="text-sm font-semibold text-gray-900">{eligible}</span>
        <span className="mx-1 text-gray-500">/</span>
        <span className="text-xs text-gray-600">{ineligible} ineligible</span>
      </div>
      {reasons.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {reasons.map((r, i) => (
            <span
              key={i}
              className={`rounded px-1 py-0 text-[10px] font-medium ${r.tone}`}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}
      {wpRemaining > 0 && (
        <div className="mt-0.5 text-[10px] text-gray-600">WP remaining: {wpRemaining}d</div>
      )}
      {wpWasActive && wpRemaining === 0 && wpConsumed > 0 && (
        <div className="mt-0.5 text-[10px] font-medium text-emerald-700">WP satisfied</div>
      )}
    </div>
  );
}

export function PaidCell({
  month,
  cumulativeToDate,
  presentation,
  terms,
}: {
  month: MonthlyEntry;
  cumulativeToDate?: number;
  presentation?: StrategyPresentation;
  terms: ClaimTerms;
}) {
  const headline = getMonthPayout(month);
  const extras = presentation?.paidCellExtras?.(month) ?? null;
  const tooltipContent = presentation?.payoutCellTooltip?.({ month, terms }) ?? null;

  // Out-of-pocket reasons for this row, as a stacked per-reason day breakdown
  // (e.g. "Less than 2 ADLs active · 18d") with rich hover tooltips. Surfaced
  // directly in the payout column now that the projection renders as one table
  // — non-paying months no longer get a dedicated pre-payout table.
  const breakdown = month.events.ineligibleDayBreakdown;
  const ineligibleDays =
    breakdown.lt2AdlDays + breakdown.inWpDays + breakdown.prePolicyDays;
  const reasonNode =
    ineligibleDays > 0 ? (
      <ReasonBreakdown
        lines={breakdownLines(breakdown)}
        fallback={reasonForMonth(month)}
        variant="detail"
        tooltips={buildReasonTooltips(month, terms)}
      />
    ) : null;

  if (headline === 0 && extras == null && tooltipContent == null) {
    return (
      <div className="leading-tight">
        <div className="text-gray-300">—</div>
        {reasonNode}
      </div>
    );
  }

  // Flag rows where the policy paid something but a portion of the month sat
  // in an out-of-pocket bucket (WP / <2 ADL / before policy start) — i.e. the
  // payout is reduced relative to what a fully eligible row would have paid.
  // The icon is a glanceable signal; the stacked reason breakdown beneath
  // spells out which buckets ate into the payout.
  const payoutReduced = headline > 0 && ineligibleDays > 0;
  const reducedReasonLabel = buildReducedReasonLabel(breakdown);

  const headlineNode = (
    <div className="font-medium text-gray-900">
      {currency(headline)}
      {payoutReduced && (
        <AlertTriangle
          className="ml-1 inline h-3 w-3 align-text-top text-amber-500"
          aria-label={`Payout reduced — ${reducedReasonLabel}`}
        />
      )}
      {cumulativeToDate !== undefined && (
        <span className="ml-1 font-normal text-gray-500">({currency(cumulativeToDate)})</span>
      )}
    </div>
  );

  return (
    <div className="leading-tight">
      {tooltipContent ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help">{headlineNode}</div>
          </TooltipTrigger>
          {tooltipContent}
        </Tooltip>
      ) : (
        headlineNode
      )}
      {reasonNode}
      {extras}
    </div>
  );
}

// Human-readable summary of which ineligible buckets ate into a paying row,
// keyed off the same `ineligibleDayBreakdown` the pre-payout table uses. Drives
// the AlertTriangle's aria-label / title — assistive tech and bare hover users
// still get a one-line explanation when the strategy tooltip isn't present.
function buildReducedReasonLabel(breakdown: IneligibleDayBreakdown): string {
  const parts: string[] = [];
  if (breakdown.lt2AdlDays > 0) parts.push(`${breakdown.lt2AdlDays}d <2 ADL`);
  if (breakdown.inWpDays > 0) parts.push(`${breakdown.inWpDays}d waiting period`);
  if (breakdown.prePolicyDays > 0)
    parts.push(`${breakdown.prePolicyDays}d before policy start`);
  return parts.join(' + ');
}

// Closing LTC pool + this-month delta. LTC pool is derived (base + eob)
// — read it via `getLtcPool`. Optional `tooltipContent` wraps in a Tooltip
// (used by strategies like Asset Flex to break out per-pool balances).
export function LtcPoolWithDeltaCell({
  openState,
  closeState,
  tooltipContent,
  unlimited,
}: {
  openState: MonthlyState;
  closeState: MonthlyState;
  tooltipContent?: ReactNode;
  unlimited?: boolean;
}) {
  return (
    <BalanceWithDeltaCell
      opening={getLtcPool(openState)}
      closing={getLtcPool(closeState)}
      tooltipContent={tooltipContent}
      unlimited={unlimited}
    />
  );
}

// Totals-row value for a POLICY-LEVEL running total (cumulative policy payout,
// pool remaining) when a joint policy is filtered to ONE claimant. These totals
// don't split by claimant, so the same number shows on the client and partner
// toggle views even though the monthly rows above are filtered — the trailing
// "both spouses*" note + tooltip make that explicit (P2-47 sibling). `caption`
// is an optional prefix (e.g. "paid to date"); pool remaining passes none.
export function CombinedSpousesTotalCell({
  value,
  caption,
  unlimited,
}: {
  value: number;
  caption?: string;
  unlimited?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help leading-tight">
          <div className="font-medium text-gray-900">
            {unlimited ? 'Unlimited' : currency(value)}
          </div>
          <div className="text-[10px] text-gray-500">
            {caption ? `${caption} · ` : ''}both spouses
            <sup className="ml-0.5">*</sup>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        Combined total across both spouses. Policy payout and pool balances are
        policy-level and don&apos;t split by claimant, so this total is unchanged on the
        client and partner views even though the monthly rows above are filtered to one
        claimant.
      </TooltipContent>
    </Tooltip>
  );
}

// Closing balance + this-row delta (neutral). Closing is the headline (matches
// financial-statement convention and the month on which milestones like
// pool-exhausted fire); delta below reflects this month's change. Delta is
// omitted when zero. Optional `tooltipContent` wraps the cell in a Tooltip.
export function BalanceWithDeltaCell({
  opening,
  closing,
  tooltipContent,
  unlimited,
}: {
  opening: number;
  closing: number;
  tooltipContent?: ReactNode;
  // When true, the pool is unlimited: the running balance is a non-meaningful
  // sentinel (~$1B minus draws), so render "Unlimited" and suppress the delta.
  unlimited?: boolean;
}) {
  const delta = closing - opening;
  const inner = unlimited ? (
    <div className="leading-tight">
      <div className="font-medium text-gray-900">Unlimited</div>
    </div>
  ) : (
    <div className="leading-tight">
      <div className="font-medium text-gray-900">{currency(closing)}</div>
      {delta !== 0 && (
        <div className="text-[10px] text-gray-500">
          {delta > 0 ? '+' : '−'}
          {currency(Math.abs(delta))}
        </div>
      )}
    </div>
  );
  if (!tooltipContent) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{inner}</div>
      </TooltipTrigger>
      {tooltipContent}
    </Tooltip>
  );
}

// Increment count for a pool's inflation factor at a given policy year,
// matching the engine's `compoundFactor`: exponent = policyYear − 1, capped at
// `capYears` (a capYears-year rider applies capYears increases).
function poolIncrements(policyYear: number, capYears: number | undefined): number {
  const cap = capYears && capYears > 0 ? capYears : null;
  const intYear = Math.floor(policyYear);
  const rawIncrements = Math.max(intYear - 1, 0);
  return cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
}

// One pool's "at issue → grown → ceiling" derivation, as grid rows. Makes a
// flat ceiling self-explanatory: a 0% pool (e.g. Asset Care "AOB None") shows
// a "No inflation" line, and a pool past its cap shows a "cap reached" note —
// instead of silently dropping the growth row and looking broken.
function PoolDerivation({
  heading,
  atIssue,
  ceiling,
  rate,
  policyYear,
  capYears,
}: {
  heading?: string;
  atIssue: number;
  ceiling: number;
  rate: number;
  policyYear: number;
  capYears: number | undefined;
}) {
  const ratePct = (rate * 100).toFixed(2);
  const intYear = Math.floor(policyYear);
  const cap = capYears && capYears > 0 ? capYears : null;
  const capped = cap !== null && intYear > cap;
  const increments = poolIncrements(policyYear, capYears);
  // "Ceiling" only makes sense once inflation is capped or the pool is flat.
  // While inflation is still active the pool keeps growing every anniversary,
  // so the row value is just the year-N total, not a ceiling.
  const isTrueCeiling = rate <= 0 || capped;
  const resultLabel = isTrueCeiling
    ? rate <= 0
      ? 'Pool'
      : 'Ceiling'
    : `Year ${intYear} value`;
  return (
    <>
      {heading && (
        <div className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
          {heading}
        </div>
      )}
      <span className="text-gray-300">At issue</span>
      <span className="font-semibold tabular-nums">{currency(atIssue)}</span>
      {rate <= 0 ? (
        <span className="col-span-2 text-[10px] text-gray-400">No inflation — flat</span>
      ) : increments > 0 ? (
        <>
          <span className="text-gray-300">Growth ({ratePct}%/yr)</span>
          <span className="font-semibold tabular-nums">
            × (1 + {ratePct}%)<sup>{increments}</sup>
          </span>
          {capped ? (
            <span className="col-span-2 text-[10px] text-amber-300">
              Inflation cap reached (year {cap})
            </span>
          ) : (
            <span className="col-span-2 text-[10px] text-gray-400">
              Grows another {ratePct}% at each future anniversary{cap ? ` until year ${cap}` : ''}
            </span>
          )}
        </>
      ) : null}
      <span className="text-gray-300">{resultLabel}</span>
      <span className="font-semibold tabular-nums">{currency(ceiling)}</span>
    </>
  );
}

function PoolLimitTooltip({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot;
  terms: ClaimTerms;
}) {
  const intYear = Math.floor(snapshot.policyYear);
  const baseAtIssue = terms.baseBenefitPoolAtIssue;
  const eobAtIssue = terms.extensionOfBenefitsPoolAtIssue;
  const total = snapshot.theoreticalLtcPoolCeiling;
  const cobCeiling = snapshot.effectiveContinuationBenefit;
  const aobCeiling = total - cobCeiling;

  // No at-issue pool concept (CM II): the base pool is flat and does not
  // inflate — its growth lives in the monthly rider instead.
  if (baseAtIssue === undefined && eobAtIssue === undefined) {
    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Pool limit · policy year {intYear}</div>
        <div className="mb-1 text-[10px] text-gray-300">
          Flat base pool — it does not inflate. The inflation rider grows the
          monthly benefit instead (see Benefit amount).
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Base pool</span>
          <span className="font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    );
  }

  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const eobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const hasEob = (eobAtIssue ?? 0) > 0 || cobCeiling > 0;
  const baseCap = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const eobCap = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;
  // "Inflated ceiling" only applies once all inflating pools have hit their
  // caps (or have no inflation). Otherwise this is the year-N value — the
  // pool would keep growing on future anniversaries.
  const baseCapped = baseRate <= 0 || (baseCap > 0 && intYear > baseCap);
  const eobCapped = !hasEob || eobRate <= 0 || (eobCap > 0 && intYear > eobCap);
  const allCapped = baseCapped && eobCapped;
  const description = allCapped
    ? 'Inflated pool size if no benefits were used. Inflation has reached its cap, so this is the true ceiling.'
    : 'Inflated pool size at this policy year if no benefits were used. The pool keeps growing on each future anniversary until any inflation cap is reached.';
  const totalLabel = allCapped ? 'Total ceiling' : `Total · year ${intYear}`;

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">Pool limit · policy year {intYear}</div>
      <div className="mb-1 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <PoolDerivation
          heading={hasEob ? 'Acceleration (AOB)' : undefined}
          atIssue={baseAtIssue ?? 0}
          ceiling={aobCeiling}
          rate={baseRate}
          policyYear={snapshot.policyYear}
          capYears={terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears}
        />
        {hasEob && (
          <PoolDerivation
            heading="Continuation (COB)"
            atIssue={eobAtIssue ?? 0}
            ceiling={cobCeiling}
            rate={eobRate}
            policyYear={snapshot.policyYear}
            capYears={terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears}
          />
        )}
        {hasEob && (
          <>
            <span className="mt-1 font-semibold text-gray-200">{totalLabel}</span>
            <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
          </>
        )}
      </div>
    </TooltipContent>
  );
}

// Theoretical inflated pool ceiling for the row's policy year — what the
// pool(s) would be if no benefits were used. Spend-independent: reads the
// engine's `theoreticalLtcPoolCeiling` for the headline and the `terms`
// at-issue amounts for the derivation. Universal across products.
export function PoolLimitCell({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot | undefined;
  terms: ClaimTerms;
}) {
  if (!snapshot) return <span className="text-gray-300">—</span>;
  // Unlimited lifetime pool: the ceiling is the sentinel (plus any finite
  // pool), not a real dollar figure — render "Unlimited" instead of ~$1B.
  if (terms.isUnlimited) {
    return <div className="font-medium leading-tight text-gray-900">Unlimited</div>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">
            {currency(snapshot.theoreticalLtcPoolCeiling)}
          </div>
        </div>
      </TooltipTrigger>
      <PoolLimitTooltip snapshot={snapshot} terms={terms} />
    </Tooltip>
  );
}

// Cumulative pool drawdown through this month, categorized: prior utilization
// (the AOB lump used before the projection) + Acceleration (AOB) + Continuation
// (COB) drawn. Total reconciles to the limit/remaining identity while a pool is
// open: Pool limit − Drawn = Pool remaining. Uses pool depletion (not cash
// paid), which can be less than payout for CM II (rider on top) / indemnity.
// Labels and copy for the Drawn tooltip body. Single-pool products (traditional
// LTC) pass `singlePool: true` and `baseLabel: 'Coverage pool drawn'` so the
// AOB/COB framing doesn't leak through; the cob row is hidden in that mode.
export interface DrawnCellLabels {
  description: string;
  baseLabel: string; // label for the `drawn.aob` row
  cobLabel?: string; // omit / hide when `singlePool`
  singlePool?: boolean;
}

const DEFAULT_DRAWN_LABELS: DrawnCellLabels = {
  description:
    'Cumulative reduction of the LTC pool. Pool limit − this = Pool remaining ' +
    '(while the pool is open). Reflects pool drawdown, which can be less than ' +
    'cash paid (indemnity / rider-on-top).',
  baseLabel: 'Acceleration (AOB) drawn',
  cobLabel: 'Continuation (COB) drawn',
};

export function CumulativeDrawnCell({
  drawn,
  priorUtilization,
  labels,
}: {
  drawn: { aob: number; cob: number } | undefined;
  priorUtilization: number;
  labels?: DrawnCellLabels;
}) {
  if (!drawn) return <span className="text-gray-300">—</span>;
  const total = priorUtilization + drawn.aob + drawn.cob;
  if (total === 0) return <span className="text-gray-300">—</span>;
  const L = labels ?? DEFAULT_DRAWN_LABELS;
  const showCob = !L.singlePool && drawn.cob > 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">{currency(total)}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Drawn down to date</div>
        <div className="mb-1 text-[10px] text-gray-300">{L.description}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {priorUtilization > 0 && (
            <>
              <span className="text-gray-300">Used before this projection</span>
              <span className="font-semibold tabular-nums">{currency(priorUtilization)}</span>
            </>
          )}
          {drawn.aob > 0 && (
            <>
              <span className="text-gray-300">{L.baseLabel}</span>
              <span className="font-semibold tabular-nums">{currency(drawn.aob)}</span>
            </>
          )}
          {showCob && L.cobLabel && (
            <>
              <span className="text-gray-300">{L.cobLabel}</span>
              <span className="font-semibold tabular-nums">{currency(drawn.cob)}</span>
            </>
          )}
          <span className="mt-1 font-semibold text-gray-200">Total drawn</span>
          <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
